import { useEffect, useState } from "react";
import { EPaymentStatus, HistoryTranslate } from "../../main/AiToolsPage";
import { IGetPageHistoryTransactionByUser } from "../../../models/payment";
import UserService from "../../../services/api/user.service";
import {
  CloseButton,
  PreviewHeader,
  PreviewModal,
  PreviewOverlay,
  PreviewTitle,
} from "../../main/StyledFileTranslatePage";
import {
  Cell,
  DownloadButton,
  FilterBar,
  HeaderRow,
  HeaderRowAdmin,
  NavButton,
  Note,
  PageButton,
  PageWrapper,
  PaginationWrapper,
  PayButton,
  RowAdmin,
  SearchInput,
  Select,
  StatusPaid,
  StatusUnpaid,
  Table,
  TableWrapper,
  Title,
} from "../../../style/TransactionHistory_Style";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/notify";

interface Props {
  open: boolean;
  onClose: () => void;
  userId?: string;
}

const UserTransactionModal = ({ open, onClose, userId }: Props) => {
  const PAGE_SIZE = 10;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<EPaymentStatus | "all">("all");
  const { t } = useTranslation();

  const [totalPages, setTotalPages] = useState(1);

  const [historyTranslates, setHistoryTranslates] = useState<
    HistoryTranslate[]
  >([]);

  useEffect(() => {
    if (open) {
      getData();
    }
  }, [page, search, status, open]);

  const formatVND = (value: number): string => {
    return new Intl.NumberFormat("vi-VN").format(value);
  };

  const formatFileSize = (bytes: number) => {
    return bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(1)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (date?: string | Date): string => {
    if (!date) return "";

    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const getData = async () => {
    const params: IGetPageHistoryTransactionByUser = {
      CurrentPage: page,
      PageSize: PAGE_SIZE,
      KeySearch: search,
      Status: status === "all" ? undefined : status,

      // backend thêm field này
      UserId: userId,
    };

    const res = await UserService.getPageFileByUserId(params);

    if (res.success) {
      setHistoryTranslates(res.items || []);
      setTotalPages(res.totalPages || 1);
    }
  };

  const downloadBase64File = (base64: string, fileName: string) => {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "application/octet-stream";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    const blob = new Blob([u8arr], { type: mime });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName; // 👈 tên file tải về
    document.body.appendChild(a);
    a.click(); // 👈 trigger download
    document.body.removeChild(a);

    URL.revokeObjectURL(url); // cleanup
  };

  const getPagination = (current: number, total: number) => {
    const delta = 2;
    const range: (number | string)[] = [];

    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    range.push(1);

    if (left > 2) {
      range.push("...");
    }

    for (let i = left; i <= right; i++) {
      range.push(i);
    }

    if (right < total - 1) {
      range.push("...");
    }

    if (total > 1) {
      range.push(total);
    }

    return range;
  };

  if (!open) return null;

  return (
    <PreviewOverlay onClick={onClose}>
      <PreviewModal
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "95%",
          maxWidth: "1400px",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}>
        {/* Header */}
        <PreviewHeader>
          <PreviewTitle>Lịch sử giao dịch</PreviewTitle>

          <CloseButton onClick={onClose}>✕</CloseButton>
        </PreviewHeader>

        {/* Content */}
        <div
          style={{
            overflow: "auto",
          }}>
          <PageWrapper
            style={{
              padding: 0,
              boxShadow: "none",
              background: "transparent",
            }}>
            <FilterBar sx={{ paddingTop: 1 }}>
              <SearchInput
                placeholder={t("transaction.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <Select
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as EPaymentStatus | "all")
                }>
                <option value="all">{t("transaction.status.all")}</option>

                <option value={EPaymentStatus.success}>
                  {t("transaction.status.paid")}
                </option>

                <option value={EPaymentStatus.pending}>
                  {t("transaction.status.unpaid")}
                </option>
              </Select>
            </FilterBar>

            <TableWrapper>
              <Table>
                <HeaderRowAdmin>
                  <Cell>{t("transaction.table.fileName")}</Cell>

                  <Cell>{t("transaction.table.fileSize")}</Cell>

                  <Cell>{t("transaction.table.language")}</Cell>

                  <Cell>{t("transaction.table.status")}</Cell>

                  <Cell>{t("transaction.table.price")}</Cell>

                  <Cell>{t("transaction.table.expirationDate")}</Cell>

                  <Cell>Ngày tạo</Cell>

                  <Cell>{t("transaction.table.action")}</Cell>
                </HeaderRowAdmin>

                {historyTranslates &&
                  historyTranslates.map((item) => (
                    <RowAdmin key={item.id}>
                      <Cell>{item.fileName}</Cell>

                      <Cell>{formatFileSize(item.fileSize)}</Cell>

                      <Cell>
                        {item.from} → {item.to}
                      </Cell>

                      <Cell>
                        {item.status === EPaymentStatus.success ? (
                          <StatusPaid>
                            {t("transaction.status.paid")}
                          </StatusPaid>
                        ) : (
                          <StatusUnpaid>
                            {t("transaction.status.unpaid")}
                          </StatusUnpaid>
                        )}
                      </Cell>

                      <Cell>{item?.price && formatVND(item?.price)} VNĐ</Cell>

                      <Cell>
                        {item?.expirationDate &&
                          formatDate(item?.expirationDate)}
                      </Cell>
                      <Cell>
                        {item?.createdDate && formatDate(item?.createdDate)}
                      </Cell>
                      <Cell>
                        <DownloadButton
                          onClick={() =>
                            downloadBase64File(
                              item.fileDownloadBase64,
                              item.fileName,
                            )
                          }>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="currentColor"
                            stroke="currentColor"
                            strokeWidth="0.8"
                            className="bi bi-download">
                            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                          </svg>
                          {t("transaction.download")}
                        </DownloadButton>
                      </Cell>
                    </RowAdmin>
                  ))}
              </Table>
            </TableWrapper>

            {totalPages > 1 && (
              <PaginationWrapper>
                <NavButton
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}>
                  {t("pagination.prev")}
                </NavButton>

                {getPagination(page, totalPages).map((item, i) =>
                  item === "..." ? (
                    <span key={i} style={{ padding: "0 8px" }}>
                      ...
                    </span>
                  ) : (
                    <PageButton
                      key={i}
                      active={page === item}
                      onClick={() => setPage(item as number)}>
                      {item}
                    </PageButton>
                  ),
                )}

                <NavButton
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}>
                  {t("pagination.next")}
                </NavButton>
              </PaginationWrapper>
            )}
          </PageWrapper>
        </div>
      </PreviewModal>
    </PreviewOverlay>
  );
};

export default UserTransactionModal;
